import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../utils/constant.js";
import { toastr } from 'react-redux-toastr';
import $ from "jquery";

const VipMembers = () => {
  const { orgId } = useParams();
  const id = useSelector((state) => {
    return orgId ? orgId : state?.summary?.localClient?._id || state?.summary?.clients?.[0]?._id;
  });

  // VIP Members state
  const [vipMembers, setVipMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [showCreateMemberModal, setShowCreateMemberModal] = useState(false);
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [newMember, setNewMember] = useState({
    name: "",
    email: "",
    position: "",
  });
  const [editMember, setEditMember] = useState({
    name: "",
    position: "",
  });
  const [emailInput, setEmailInput] = useState("");
  const [editEmailInput, setEditEmailInput] = useState("");
  const [memberFilter, setMemberFilter] = useState("");
  const [filteredMemberId, setFilteredMemberId] = useState("");

  const vendorName = sessionStorage.getItem("vendorName");
  const vendorId = sessionStorage.getItem("vendorId");

  // Validate email helper
  const validateEmail = (email) => {
    if (!email || typeof email !== "string") return null;
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return null;
    
    // Basic email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) return null;
    
    return trimmedEmail;
  };

  // Normalize emails - handle arrays, comma-separated strings, or single email
  const normalizeEmails = (email) => {
    if (!email) return [];
    if (Array.isArray(email)) return email.filter(e => e && e.trim());
    if (typeof email === "string") {
      // Check if it's comma-separated
      if (email.includes(",")) {
        return email.split(",").map(e => e.trim()).filter(e => e);
      }
      return [email.trim()];
    }
    return [];
  };

  // Render emails helper
  const renderEmails = (email) => {
    const emails = normalizeEmails(email);
    
    if (emails.length === 0) {
      return <span className="text-muted">N/A</span>;
    }
    
    if (emails.length === 1) {
      const safeEmail = validateEmail(emails[0]);
      return safeEmail ? (
        <a href={`mailto:${safeEmail}`} style={{ wordBreak: "break-word" }}>
          {emails[0]}
        </a>
      ) : (
        <span style={{ wordBreak: "break-word" }}>{emails[0]}</span>
      );
    }
    
    // Multiple emails - display differently
    return (
      <div style={{ fontSize: "11px" }}>
        {emails.map((emailItem, idx) => {
          const safeEmail = validateEmail(emailItem);
          return (
            <div key={idx} style={{ marginBottom: "4px" }}>
              {safeEmail ? (
                <a href={`mailto:${safeEmail}`} style={{ wordBreak: "break-word" }}>
                  {emailItem}
                </a>
              ) : (
                <span style={{ wordBreak: "break-word" }}>{emailItem}</span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const handleViewReport = async (memberId) => {
    if (!id || !memberId) {
      toastr.error('Error', 'Missing organisation or VIP member identifier');
      return;
    }

    const reportUrl = `${API_URL}/executive-monitoring/${id}/${memberId}/pdf`;

    try {
      const session = localStorage.getItem("session");
      const response = await axios.get(reportUrl, {
        responseType: "arraybuffer",
        headers: {
          Authorization: `Bearer ${session}`,
          Accept: "application/pdf"
        }
      });

      const contentType = response.headers?.["content-type"] || "application/pdf";
      const blob = new Blob([response.data], { type: contentType });
      const pdfUrl = window.URL.createObjectURL(blob);

      // Trigger download
      // const link = document.createElement("a");
      // link.href = pdfUrl;
      // link.download = `vip-member-${memberId}-report.pdf`;
      // document.body.appendChild(link);
      // link.click();
      // link.remove();

      // Also open in new tab for direct view
      const newTab = window.open(pdfUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(pdfUrl), 30000);
    } catch (error) {
      console.error("Error fetching PDF report:", error);
      // If backend returned text/html error, surface it
      if (error?.response?.data) {
        try {
          const decoder = new TextDecoder();
          const text = decoder.decode(error.response.data);
          toastr.error('Error', text || 'Failed to open PDF report');
          return;
        } catch (_) {
          // fall through
        }
      }
      const message = error.response?.data?.userMessage || 'Failed to open PDF report';
      toastr.error('Error', message);
    }
  };

  // Fetch VIP Members
  const fetchVipMembers = async () => {
    if (!id) return;
    setLoadingMembers(true);
    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("session")}`;
      const url = filteredMemberId
        ? `${API_URL}/executive-monitoring/${id}/vip-members?vipMemberId=${filteredMemberId}`
        : `${API_URL}/executive-monitoring/${id}/vip-members`;
      const response = await axios.get(url);
      setVipMembers(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error("Error fetching VIP members:", error);
      setVipMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Parse email input into array
  const parseEmails = (input) => {
    if (!input || !input.trim()) return [];
    // Split by comma or newline, trim each, and filter out empty strings
    return input
      .split(/[,\n]/)
      .map(email => email.trim())
      .filter(email => email.length > 0);
  };

  // Create VIP Member
  const createVipMember = async () => {
    try {
      const emails = parseEmails(emailInput);
      if (emails.length === 0) {
        toastr.error('Error', 'Please enter at least one email address');
        return;
      }

      axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("session")}`;
      const memberData = {
        ...newMember,
        email: emails
      };
      const response = await axios.post(
        `${API_URL}/executive-monitoring/${id}/vip-member`,
        memberData
      );
      if (response.data) {
        setShowCreateMemberModal(false);
        setNewMember({ name: "", email: "", position: "" });
        setEmailInput("");
        fetchVipMembers();
        toastr.success('Success', 'VIP member created successfully');
      }
    } catch (error) {
      console.error("Error creating VIP member:", error);
      toastr.error('Error', error.response?.data?.userMessage || "Failed to create VIP member");
    }
  };

  // Handle edit button click
  const handleEditMember = (member) => {
    setEditingMember(member);
    setEditMember({
      name: member.name || "",
      position: member.position || "",
    });
    // Pre-populate with existing emails
    const existingEmails = normalizeEmails(member.email);
    setEditEmailInput(existingEmails.join("\n"));
    setShowEditMemberModal(true);
  };

  // Update VIP Member
  const updateVipMember = async () => {
    if (!editingMember) return;
    
    try {
      const emails = parseEmails(editEmailInput);
      if (emails.length === 0) {
        toastr.error('Error', 'Please enter at least one email address');
        return;
      }
      
      axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("session")}`;
      const memberId = editingMember._id || editingMember.id;
      const updateData = {
        name: editMember.name,
        email: emails, // All emails (edited existing + any new ones)
        position: editMember.position
      };
      
      const response = await axios.put(
        `${API_URL}/executive-monitoring/${id}/vip-member/${memberId}`,
        updateData
      );
      if (response.data) {
        setShowEditMemberModal(false);
        setEditingMember(null);
        setEditMember({ name: "", position: "" });
        setEditEmailInput("");
        fetchVipMembers();
        toastr.success('Success', 'VIP member updated successfully');
      }
    } catch (error) {
      console.error("Error updating VIP member:", error);
      toastr.error('Error', error.response?.data?.userMessage || "Failed to update VIP member");
    }
  };

  // Filter members by ID
  const handleFilterMembers = () => {
    setFilteredMemberId(memberFilter);
  };

  // Clear filter
  const clearFilter = () => {
    setMemberFilter("");
    setFilteredMemberId("");
  };

  // Delete VIP Member
  const deleteVipMember = (memberId, memberName) => {
    $.confirm({
      title: "Are you sure?",
      content: `Do you really want to delete VIP member "${memberName || 'this member'}"? This action cannot be undone.`,
      type: "red",
      buttons: {
        ok: {
          text: "Delete",
          btnClass: "btn-danger",
          keys: ["enter"],
          action: async function () {
            try {
              axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("session")}`;
              const response = await axios.delete(
                `${API_URL}/executive-monitoring/${id}/vip-member/${memberId}`
              );
              if (response.data) {
                toastr.success('Success', response.data.userMessage || 'VIP member deleted successfully');
                fetchVipMembers();
              }
            } catch (error) {
              console.error("Error deleting VIP member:", error);
              toastr.error('Error', error.response?.data?.userMessage || "Failed to delete VIP member");
            }
          },
        },
        cancel: function () {},
      },
    });
  };

  useEffect(() => {
    if (id) {
      fetchVipMembers();
    }
  }, [id, filteredMemberId]);

  return (
    <div className="container-fluid">
      <div className="block-header">
        <div className="row clearfix">
          <div className="col-md-6 col-sm-12">
            <h1 className="welcome-text">VIP Members</h1>
            <nav aria-label="breadcrumb" className="mt-1">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  {vendorId ? (
                    <Link to="/vendor" onClick={() => sessionStorage.clear()}>
                      Forest View
                    </Link>
                  ) : (
                    <Link to="/" className="welcome-textSmall">
                      Home
                    </Link>
                  )}
                </li>
                {vendorName && (
                  <li className="breadcrumb-item active" aria-current="page" style={{ color: "white" }}>
                    {vendorName}
                  </li>
                )}
                <li className="breadcrumb-item active" aria-current="page" style={{ color: "white" }}>
                  VIP Members
                </li>
              </ol>
              <br />
            </nav>
          </div>
        </div>
      </div>

      {/* VIP Members Section */}
      <div className="card mb-4">
        <div className="body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="welcome-textSmall">VIP Members Management</h6>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowCreateMemberModal(true)}
            >
              <i className="fa fa-plus mr-1"></i>Add VIP Member
            </button>
          </div>

          {/* Filter Section */}
          <div className="row mb-3">
            <div className="col-md-6">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Filter by VIP Member ID"
                  value={memberFilter}
                  onChange={(e) => setMemberFilter(e.target.value)}
                />
                <div className="input-group-append">
                  <button
                    className="btn btn-primary btn-sm"
                    type="button"
                    onClick={handleFilterMembers}
                  >
                    Filter
                  </button>
                  {filteredMemberId && (
                    <button
                      className="btn btn-secondary btn-sm"
                      type="button"
                      onClick={clearFilter}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* VIP Members Table */}
          {loadingMembers ? (
            <div className="text-center p-4">
              <i className="fa fa-spinner fa-spin"></i> Loading members...
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-custom spacing5 mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Position</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vipMembers.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center">
                        No VIP members found
                      </td>
                    </tr>
                  ) : (
                    vipMembers.map((member, index) => (
                      <tr key={member._id || index}>
                        <td>{member.name || "N/A"}</td>
                        <td style={{ padding: "12px 8px", verticalAlign: "middle", wordBreak: "break-word" }}>
                          {renderEmails(member.email)}
                        </td>
                        <td>{member.position || "N/A"}</td>
                        <td>
                          {member.createdAt
                            ? new Date(member.createdAt).toLocaleString()
                            : "N/A"}
                        </td>
                        <td>
                          <div className="btn-group" role="group">
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => handleViewReport(member._id || member.id)}
                              title="View Report"
                              style={{ marginRight: "5px" }}
                            >
                              View Report
                            </button>
                            <button
                              className="btn btn-info btn-sm"
                              onClick={() => handleEditMember(member)}
                              title="Edit VIP Member"
                              style={{ marginRight: "5px" }}
                            >
                              <i className="fa fa-edit"></i>
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => deleteVipMember(member._id || member.id, member.name)}
                              title="Delete VIP Member"
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create VIP Member Modal */}
      {showCreateMemberModal && (
        <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New VIP Member</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => {
                    setShowCreateMemberModal(false);
                    setNewMember({ name: "", email: "", position: "" });
                    setEmailInput("");
                  }}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newMember.name}
                    onChange={(e) =>
                      setNewMember({ ...newMember, name: e.target.value })
                    }
                    placeholder="Enter name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email(s) *</label>
                  <textarea
                    className="form-control"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="Enter email addresses (comma or newline separated)"
                    rows={4}
                    required
                  />
                  <small className="form-text text-muted">
                    Enter multiple emails separated by commas or new lines
                  </small>
                </div>
                <div className="form-group">
                  <label>Position *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newMember.position}
                    onChange={(e) =>
                      setNewMember({ ...newMember, position: e.target.value })
                    }
                    placeholder="Enter position"
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreateMemberModal(false);
                    setNewMember({ name: "", email: "", position: "" });
                    setEmailInput("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={createVipMember}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {showCreateMemberModal && (
        <div
          className="modal-backdrop fade show"
          onClick={() => {
            setShowCreateMemberModal(false);
            setNewMember({ name: "", email: "", position: "" });
            setEmailInput("");
          }}
        ></div>
      )}

      {/* Edit VIP Member Modal */}
      {showEditMemberModal && editingMember && (
        <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit VIP Member</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => {
                    setShowEditMemberModal(false);
                    setEditingMember(null);
                    setEditMember({ name: "", position: "" });
                    setEditEmailInput("");
                  }}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editMember.name}
                    onChange={(e) =>
                      setEditMember({ ...editMember, name: e.target.value })
                    }
                    placeholder="Enter name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email(s) *</label>
                  <textarea
                    className="form-control"
                    value={editEmailInput}
                    onChange={(e) => setEditEmailInput(e.target.value)}
                    placeholder="Enter email addresses (comma or newline separated)"
                    rows={4}
                    required
                  />
                  <small className="form-text text-muted">
                    Enter all email addresses separated by commas or new lines. You can edit existing emails or add new ones.
                  </small>
                </div>
                <div className="form-group">
                  <label>Position *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editMember.position}
                    onChange={(e) =>
                      setEditMember({ ...editMember, position: e.target.value })
                    }
                    placeholder="Enter position"
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEditMemberModal(false);
                    setEditingMember(null);
                    setEditMember({ name: "", position: "" });
                    setEditEmailInput("");
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={updateVipMember}
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal Backdrop */}
      {showEditMemberModal && (
        <div
          className="modal-backdrop fade show"
          onClick={() => {
            setShowEditMemberModal(false);
            setEditingMember(null);
            setEditMember({ name: "", position: "" });
            setEditEmailInput("");
          }}
        ></div>
      )}
    </div>
  );
};

export default VipMembers;

